import { z } from 'zod';

import { client } from '@@api/client';
import { CONTENT_LANGUAGE_HEADER } from '@@api/constants/headers';
import { getAuthorIdFromSearchValue } from '@@components/AuthorSearchableAutocomplete/utils';
import { overviewListSearchParams } from '@@components/OverviewList/overviewListSearchParams';
import config from '@@config';
import ContentStatus from '@@constants/ContentStatus';
import { type ContentLanguage } from '@@lib/i18n/constants';

import { DEFAULT_REFETCH_INTERVAL } from '../constants';

const DEFAULT_SORT = 'targetDateTime,asc';

/** The proofreading tab's search. It shows proofread content unless the user picks a status. */
export const proofreadingSearchParams = overviewListSearchParams.extend({
    contentStatus: z
        .array(z.enum(ContentStatus))
        .min(1)
        .default([ContentStatus.PROOFREAD])
        .catch([ContentStatus.PROOFREAD]),
});

type ProofreadingSearchParams = z.output<typeof proofreadingSearchParams>;

/** What the proofreading list saves, apart from the other lists */
export const PROOFREADING_SAVED_KEYS = [
    'q',
    'tenantIds',
    'userIds',
    'publicationStatus',
    'contentStatus',
    'categoryIds',
    'tagIds',
] as const;

// With a text, the API ranks by relevance unless the user picked a column
export const proofreadingSort = (search: ProofreadingSearchParams) =>
    search.sort ?? (search.q ? undefined : DEFAULT_SORT);

export const proofreadingQuery = (
    search: ProofreadingSearchParams,
    contentLocale: ContentLanguage,
) =>
    client.metadata.metadata.getAll.queryOptions(
        {
            query: {
                useSearch: true,
                q: search.q,
                sort: proofreadingSort(search),
                tenantIds: search.tenantIds?.join('|'),
                userIds: search.userIds ? getAuthorIdFromSearchValue(search.userIds) : undefined,
                contentStatus: search.contentStatus,
                publicationStatus: search.publicationStatus,
                categoryIds: search.categoryIds?.join('|'),
                tagIds: search.tagIds,
                publishedAfter: search.publishedAfter,
                publishedBefore: search.publishedBefore,
                page: (search.page ?? 1) - 1,
                size: config.list.itemsPerPage,
            },
            headers: { [CONTENT_LANGUAGE_HEADER]: contentLocale },
        },
        { refetchInterval: DEFAULT_REFETCH_INTERVAL },
    );
